import React, { useState, useEffect } from 'react';
import { TensionFormData, Tension } from '../types';
import './TensionForm.css';

interface TensionFormProps {
  onSubmit: (data: TensionFormData) => Promise<void>;
  onCancel: () => void;
  initialData?: Tension;
}

const TensionForm: React.FC<TensionFormProps> = ({ onSubmit, onCancel, initialData }) => {
  const [formData, setFormData] = useState<TensionFormData>({
    systolic: '',
    diastolic: '',
    pulse: '',
    notes: '',
    datetime: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      // Convert separate date and time to datetime-local format
      const datetime = `${initialData.date}T${initialData.time}`;
      setFormData({
        systolic: initialData.systolic,
        diastolic: initialData.diastolic,
        pulse: initialData.pulse || '',
        notes: initialData.notes || '',
        datetime: datetime
      });
    } else {
      // Valores por defecto para nuevo registro - usar hora local del usuario
      const now = new Date();
      // Convertir a hora local del usuario respetando su zona horaria
      const currentDatetime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16); // Format: YYYY-MM-DDTHH:MM
      
      setFormData(prev => ({
        ...prev,
        datetime: currentDatetime
      }));
    }
  }, [initialData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validar presión sistólica
    if (!formData.systolic) {
      newErrors.systolic = 'La presión sistólica es obligatoria';
    } else if (typeof formData.systolic === 'number' && (formData.systolic < 50 || formData.systolic > 300)) {
      newErrors.systolic = 'La presión sistólica debe estar entre 50 y 300 mmHg';
    }

    // Validar presión diastólica
    if (!formData.diastolic) {
      newErrors.diastolic = 'La presión diastólica es obligatoria';
    } else if (typeof formData.diastolic === 'number' && (formData.diastolic < 30 || formData.diastolic > 200)) {
      newErrors.diastolic = 'La presión diastólica debe estar entre 30 y 200 mmHg';
    }

    // Validar que la sistólica sea mayor que la diastólica
    if (formData.systolic && formData.diastolic && 
        typeof formData.systolic === 'number' && typeof formData.diastolic === 'number' &&
        formData.systolic <= formData.diastolic) {
      newErrors.systolic = 'La presión sistólica debe ser mayor que la diastólica';
    }

    // Validar pulso si se proporciona
    if (formData.pulse && typeof formData.pulse === 'number' && 
        (formData.pulse < 30 || formData.pulse > 200)) {
      newErrors.pulse = 'El pulso debe estar entre 30 y 200 ppm';
    }

    // Validar fecha y hora
    if (!formData.datetime) {
      newErrors.datetime = 'La fecha y hora son obligatorias';
    }

    // Validar notas
    if (formData.notes && formData.notes.length > 500) {
      newErrors.notes = 'Las notas no pueden exceder 500 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getTensionCategory = (systolic: number, diastolic: number): { category: string; color: string; description: string } => {
    if (systolic < 120 && diastolic < 80) {
      return { category: 'Normal', color: '#10b981', description: 'Presión arterial normal' };
    } else if (systolic >= 120 && systolic <= 129 && diastolic < 80) {
      return { category: 'Elevada', color: '#f59e0b', description: 'Presión arterial elevada' };
    } else if ((systolic >= 130 && systolic <= 139) || (diastolic >= 80 && diastolic <= 89)) {
      return { category: 'Hipertensión Grado 1', color: '#f97316', description: 'Hipertensión en etapa 1' };
    } else if (systolic >= 140 || diastolic >= 90) {
      return { category: 'Hipertensión Grado 2', color: '#ef4444', description: 'Hipertensión en etapa 2' };
    } else {
      return { category: 'Indeterminada', color: '#6b7280', description: 'Valores atípicos' };
    }
  };

  const handleInputChange = (field: keyof TensionFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar errores del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Convertir strings vacíos a números o undefined según corresponda
      // Split datetime into date and time for backend compatibility
      const [date, time] = formData.datetime?.split('T') || ['', ''];
      const submitData: TensionFormData = {
        systolic: typeof formData.systolic === 'string' ? parseInt(formData.systolic) : formData.systolic,
        diastolic: typeof formData.diastolic === 'string' ? parseInt(formData.diastolic) : formData.diastolic,
        pulse: formData.pulse ? 
          (typeof formData.pulse === 'string' ? parseInt(formData.pulse as string) : formData.pulse) : 
          undefined,
        notes: formData.notes || undefined,
        date: date,
        time: time
      };

      await onSubmit(submitData);
    } catch (error: any) {
      setErrors({ submit: error.message || 'Error al guardar la tensión' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mostrar categoría de tensión si ambos valores están completos
  const showCategory = formData.systolic && formData.diastolic && 
    typeof formData.systolic === 'number' && typeof formData.diastolic === 'number';
  const category = showCategory ? getTensionCategory(formData.systolic as number, formData.diastolic as number) : null;

  return (
    <div className="tension-form">
      <form onSubmit={handleSubmit} className="form">
        {errors.submit && (
          <div className="error-message">
            {errors.submit}
          </div>
        )}

        {/* Presión Arterial */}
        <div className="form-section">
          <h3 className="section-title">🩺 Presión Arterial</h3>
          
          {/* Fecha y Hora */}
          <div className="form-field">
            <label htmlFor="datetime" className="form-label">
              📅 Fecha y hora *
            </label>
            <input
              type="datetime-local"
              id="datetime"
              className={`form-input ${errors.datetime ? 'error' : ''}`}
              value={formData.datetime}
              onChange={(e) => handleInputChange('datetime', e.target.value)}
              required
            />
            {errors.datetime && <span className="field-error">{errors.datetime}</span>}
          </div>
          
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="systolic" className="form-label">
                Sistólica (Alta) *
              </label>
              <div className="input-with-unit">
                <input
                  type="number"
                  id="systolic"
                  className={`form-input ${errors.systolic ? 'error' : ''}`}
                  value={formData.systolic}
                  onChange={(e) => handleInputChange('systolic', e.target.value)}
                  placeholder="120"
                  min="50"
                  max="300"
                  required
                />
                <span className="input-unit">mmHg</span>
              </div>
              {errors.systolic && <span className="field-error">{errors.systolic}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="diastolic" className="form-label">
                Diastólica (Baja) *
              </label>
              <div className="input-with-unit">
                <input
                  type="number"
                  id="diastolic"
                  className={`form-input ${errors.diastolic ? 'error' : ''}`}
                  value={formData.diastolic}
                  onChange={(e) => handleInputChange('diastolic', e.target.value)}
                  placeholder="80"
                  min="30"
                  max="200"
                  required
                />
                <span className="input-unit">mmHg</span>
              </div>
              {errors.diastolic && <span className="field-error">{errors.diastolic}</span>}
            </div>
          </div>

          {/* Categoría de tensión */}
          {category && (
            <div className="tension-category" style={{ borderLeftColor: category.color }}>
              <div className="category-info">
                <span className="category-name" style={{ color: category.color }}>
                  {category.category}
                </span>
                <span className="category-description">{category.description}</span>
              </div>
            </div>
          )}
        </div>

        {/* Pulso */}
        <div className="form-field">
          <label htmlFor="pulse" className="form-label">
            💗 Pulso (opcional)
          </label>
          <div className="input-with-unit">
            <input
              type="number"
              id="pulse"
              className={`form-input ${errors.pulse ? 'error' : ''}`}
              value={formData.pulse}
              onChange={(e) => handleInputChange('pulse', e.target.value)}
              placeholder="70"
              min="30"
              max="200"
            />
            <span className="input-unit">ppm</span>
          </div>
          {errors.pulse && <span className="field-error">{errors.pulse}</span>}
        </div>

        {/* Notas */}
        <div className="form-field">
          <label htmlFor="notes" className="form-label">
            📝 Notas (opcional)
          </label>
          <textarea
            id="notes"
            className={`form-textarea ${errors.notes ? 'error' : ''}`}
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Ejercicio, medicación, síntomas, etc..."
            rows={3}
            maxLength={500}
          />
          <div className="character-count">
            {formData.notes?.length || 0}/500 caracteres
          </div>
          {errors.notes && <span className="field-error">{errors.notes}</span>}
        </div>

        {/* Botones */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-success"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="btn-spinner"></span>
                {initialData ? 'Actualizando...' : 'Guardando...'}
              </>
            ) : (
              <>
                <span className="btn-icon">💾</span>
                {initialData ? 'Actualizar Tensión' : 'Guardar Tensión'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TensionForm;